import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, X } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import Button from '../UI/Button';

interface SendByNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (sendByName: boolean) => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}

const SendByNameModal: React.FC<SendByNameModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Send WhatsApp Message',
  message = 'How would you like to address the recipients? Language (English or Arabic) comes from each guest\'s invitation setting, not this choice.',
  confirmText = 'Send',
  cancelText = 'Cancel',
  isLoading = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [sendByName, setSendByName] = useState<boolean>(true);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Reset to default when opening
      setSendByName(true);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isLoading]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!isLoading && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm(sendByName);
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      {/* Modal */}
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div
          className={`relative w-full transform overflow-hidden rounded-lg shadow-xl transition-all ${
            theme === 'dark' 
              ? 'bg-gray-900/95 border border-gray-700/50' 
              : 'bg-white border border-gray-200/50'
          }`}
        >
          {/* Content */}
          <div className="px-6 py-5">
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 flex items-center justify-center rounded-full p-2 ${
                theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}>
                <MessageCircle className={`h-5 w-5 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>

              <div className="flex-1">
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {title}
                </h3>
                <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {message}
                </p>
              </div>

              {!isLoading && (
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className={`p-1.5 rounded-lg transition-colors ${
                    theme === 'dark' 
                      ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Radio Options */}
            <div className="mt-6 space-y-3">
              <label
                className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  sendByName
                    ? theme === 'dark'
                      ? 'border-blue-500 bg-blue-900/20'
                      : 'border-blue-500 bg-blue-50'
                    : theme === 'dark'
                    ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'
                    : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                }`}
                onClick={() => setSendByName(true)}
              >
                <input
                  type="radio"
                  name="sendByName"
                  checked={sendByName}
                  onChange={() => setSendByName(true)}
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Send with guest names
                  </div>
                  <div className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Use the guest’s name in the invitation (personalized greeting).
                  </div>
                </div>
              </label>

              <label
                className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  !sendByName
                    ? theme === 'dark'
                      ? 'border-blue-500 bg-blue-900/20'
                      : 'border-blue-500 bg-blue-50'
                    : theme === 'dark'
                    ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'
                    : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                }`}
                onClick={() => setSendByName(false)}
              >
                <input
                  type="radio"
                  name="sendByName"
                  checked={!sendByName}
                  onChange={() => setSendByName(false)}
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Send with generic greetings
                  </div>
                  <div className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Use a generic greeting (no per-guest name), e.g. family & friends wording in your approved template.
                  </div>
                </div>
              </label>
            </div>

            {/* Actions */}
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                {cancelText}
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={isLoading}
                loading={isLoading}
              >
                {confirmText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SendByNameModal;

